import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { ButtonKind, ButtonSize, ButtonVariant, IconButton, IconButtonLink } from "@stubhub/broadway-web";
import { ChevronLeftIcon, ChevronRightIcon } from "@stubhub/broadway-web/icons/functional";
import type { FeaturedShowEvent } from "src/http/types";
import { FormatContent, useContent } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import { useInView } from "src/shared/hooks/useInView";
import {
  CarouselId,
  CarouselInteractionType,
  CarouselItemType,
  sendCarouselInteractionUAE,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { useCarouselAnalytics } from "src/website-apps/shared/components/carousel-module/use-carousel-analytics";
import { useCarouselItemInView } from "src/website-apps/shared/components/carousel-module/use-carousel-item-in-view";
import { featuredShowEventsModule } from "./FeaturedShowEventsModule.styles";

export interface FeaturedShowEventsModuleShow {
  name: string;
  moduleKicker?: string | undefined;
  profileImageUrl?: string | undefined;
  /** The show's landing URL. When set, the phone header arrow navigates to it instead of paging. */
  url?: string | undefined;
}

interface Props {
  show: FeaturedShowEventsModuleShow;
  events: readonly FeaturedShowEvent[];
  /** The page's category id, reported as the CarouselInteraction `categoryId`. */
  categoryId?: number | undefined;
  /** The show's entity id, reported as the CTA CarouselInteraction `itemId`. */
  entityId?: number | undefined;
  /** The promo banner's arm-specific CarouselInteraction id for the phone header CTA. */
  carouselId: CarouselId;
}

interface CardProps {
  event: FeaturedShowEvent;
  index: number;
  itemCount: number;
  categoryId?: number | undefined;
  onActivate: (itemId: number, position: number) => void;
  onHover: (itemId: number, position: number) => void;
}

/**
 * A single event card. Split out of the module so the per-item in-view hook can be called once per
 * item (hooks cannot be called in a loop).
 */
const FeaturedShowEventCard: FC<CardProps> = ({ event, index, itemCount, categoryId, onActivate, onHover }) => {
  const styles = featuredShowEventsModule();
  const inViewRef = useCarouselItemInView({
    carouselId: CarouselId.FeaturedShowEvents,
    itemId: event.eventId,
    itemIndex: index,
    itemCount,
    itemType: CarouselItemType.Event,
    categoryId,
  });

  return (
    <a
      ref={inViewRef}
      className={styles.card}
      href={event.url}
      onClick={() => onActivate(event.eventId, index)}
      onMouseEnter={() => onHover(event.eventId, index)}
    >
      <span className={styles.cardTop}>
        <span className={styles.cardText}>
          <span className={styles.date}>{event.dateLabel}</span>
          {event.locationLabel ? <span className={styles.location}>{event.locationLabel}</span> : null}
        </span>
      </span>
      {event.formattedFromPrice ? (
        // "From {0}" — {0} is the formatted price.
        <span className={styles.price}>
          <FormatContent id="cid$#4434602#" params={event.formattedFromPrice} />
        </span>
      ) : null}
    </a>
  );
};

/**
 * Featured-show event carousel (CRH-762 req 3). Server-rendered cards below the promo hero; the
 * whole card links through to the event and the price is omitted when the event has none.
 *
 * Emits the shared CarouselInteraction UAE (in-view, arrow, swipe, click, hover) under
 * {@link CarouselId.FeaturedShowEvents}. It deliberately does not use the shared ResponsiveCarousel,
 * which would additionally emit its legacy page events.
 */
export const FeaturedShowEventsModule: FC<Props> = ({ show, events, categoryId, entityId, carouselId }) => {
  const styles = featuredShowEventsModule();
  const nextEventsLabel = useContent("cid$#4434604#"); // "Next events"
  const previousEventsLabel = useContent("cid$#4434605#"); // "Previous events"
  const rowRef = useRef<HTMLDivElement>(null);
  const [showPrevious, setShowPrevious] = useState(false);
  const [showNext, setShowNext] = useState(false);

  const appContext = useAppContext();
  const { env } = appContext;
  const isPhone = !useMediaQuery(Breakpoint.Tablet);

  const { carouselInViewRef, fireArrowClickEvent, fireItemClick, fireItemHover } = useCarouselAnalytics({
    scrollContainerRef: rowRef,
    itemCount: events.length,
    carouselId: CarouselId.FeaturedShowEvents,
    itemType: CarouselItemType.Event,
    categoryId,
  });

  // CRH-762: on phone the v2 header arrow is the promo CTA (v1's CTA lives in the hero), so it
  // emits the same view/click CarouselInteraction UAEs as the desktop CTA card.
  // Phone-gated: on desktop the header is part of the module, not the CTA.
  const { ref: ctaInViewRef } = useInView({
    triggerOnce: true,
    threshold: 0.5,
    onChange: (inView) => {
      if (!inView || !isPhone) return;
      sendCarouselInteractionUAE(appContext, env, {
        carouselId,
        interactionType: CarouselInteractionType.InView,
        itemType: CarouselItemType.Category,
        categoryId,
        itemCount: 1,
      });
    },
  });

  const handleCtaClick = useCallback(() => {
    if (!isPhone) return;
    sendCarouselInteractionUAE(appContext, env, {
      carouselId,
      interactionType: CarouselInteractionType.Click,
      itemType: CarouselItemType.Category,
      itemId: entityId,
      itemIndex: 0,
      categoryId,
      itemCount: 1,
    });
  }, [appContext, env, isPhone, entityId, categoryId, carouselId]);

  // Arrows follow the shared carousel: each shows only while its edge card is clipped.
  useEffect(() => {
    const element = rowRef.current;
    if (!element || events.length === 0 || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === element.firstElementChild) {
            setShowPrevious(!entry.isIntersecting || entry.intersectionRatio < 0.99);
          } else if (entry.target === element.lastElementChild) {
            setShowNext(!(entry.isIntersecting && entry.intersectionRatio >= 0.99));
          }
        }
      },
      { root: element, threshold: [0, 0.99, 1] },
    );
    if (element.firstElementChild) {
      observer.observe(element.firstElementChild);
    }
    if (element.lastElementChild) {
      observer.observe(element.lastElementChild);
    }
    return () => observer.disconnect();
  }, [events]);

  const scrollByPage = useCallback(
    (direction: 1 | -1) => {
      const element = rowRef.current;
      if (element) {
        fireArrowClickEvent(direction === 1 ? "right" : "left");
        element.scrollBy({ left: element.clientWidth * direction, behavior: "smooth" });
      }
    },
    [fireArrowClickEvent],
  );

  if (events.length === 0) {
    return null;
  }

  const header = (
    <div className={styles.header} ref={ctaInViewRef}>
      {show.profileImageUrl ? (
        <img className={styles.profile} src={show.profileImageUrl} alt="" loading="lazy" />
      ) : null}
      <div className={styles.headerText}>
        {show.moduleKicker ? <span className={styles.kicker}>{show.moduleKicker}</span> : null}
        <span className={styles.title}>{show.name}</span>
      </div>
      {show.url ? (
        // Phone: the header arrow goes to the show's landing page, carrying the same New York
        // override as the CTA, rather than paging the carousel. It is navigation, not paging, so it
        // stays visible regardless of scroll position (unlike the carousel's own next arrow).
        <IconButtonLink
          href={show.url}
          onClick={handleCtaClick}
          icon={ChevronRightIcon}
          classes={{ root: styles.headerArrow }}
          aria-label={nextEventsLabel}
          kind={ButtonKind.Neutral}
          variant={ButtonVariant.Outline}
          size={ButtonSize.Small}
        />
      ) : showNext ? (
        <IconButton
          icon={ChevronRightIcon}
          onClick={() => scrollByPage(1)}
          classes={{ root: styles.headerArrow }}
          aria-label={nextEventsLabel}
          kind={ButtonKind.Neutral}
          variant={ButtonVariant.Outline}
          size={ButtonSize.Small}
        />
      ) : null}
    </div>
  );

  return (
    <section className={styles.root} aria-label={show.name}>
      {header}
      <div className={styles.carousel} ref={carouselInViewRef}>
        {showPrevious ? (
          <IconButton
            icon={ChevronLeftIcon}
            onClick={() => scrollByPage(-1)}
            classes={{ root: styles.prevArrow }}
            aria-label={previousEventsLabel}
            kind={ButtonKind.Neutral}
            variant={ButtonVariant.Outline}
            size={ButtonSize.Small}
          />
        ) : null}
        <div className={styles.row} ref={rowRef}>
          {events.map((event, index) => (
            <FeaturedShowEventCard
              key={event.eventId}
              event={event}
              index={index}
              itemCount={events.length}
              categoryId={categoryId}
              onActivate={fireItemClick}
              onHover={fireItemHover}
            />
          ))}
        </div>
        {showNext ? (
          <IconButton
            icon={ChevronRightIcon}
            onClick={() => scrollByPage(1)}
            classes={{ root: styles.nextArrow }}
            aria-label={nextEventsLabel}
            kind={ButtonKind.Neutral}
            variant={ButtonVariant.Outline}
            size={ButtonSize.Small}
          />
        ) : null}
      </div>
    </section>
  );
};
